import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
    SectionNav,
    RecallSection,
    TrailerSection,
    StorySection,
    ControlSection,
    ReactionsSection,
    ScreenshotSection,
    ModSection,
    Footer
} from '../../components';
import connectToStores from 'alt-utils/lib/connectToStores';
import {FullpageStore, HomeStore, HeaderStore} from '../../stores';
import {FullpageActions} from '../../actions';
import throttle from 'lodash.throttle';
import {getWindowHeight, getWindowWidth} from 'epic-common/src/browser';
import root from 'window-or-global';

@connectToStores
export default class HomeView extends React.PureComponent {
    static propTypes = {
        fullpageStore: PropTypes.object,
        localizationStore: PropTypes.object,
        homeStore: PropTypes.object,
        mobile: PropTypes.bool,
        tablet: PropTypes.bool,
        location: PropTypes.object
    };

    static getStores() {
        return [FullpageStore, HomeStore, HeaderStore];
    }

    static getPropsFromStores() {
        return {
            fullpageStore: FullpageStore.getState(),
            homeStore: HomeStore.getState(),
            mobile: HeaderStore.getState().mobile(),
            tablet: HeaderStore.getState().tablet()
        };
    }

    state = {
        height: -1,
        width: -1,
        resizeDirty: false,
        rebuildDirty: false
    };

    //check for reaction section to update anchors
    oldAnchors = ['recall', 'trailer', 'story', 'gameplay', 'screenshots', 'reaction', 'footer'];
    newAnchors = ['recall', 'trailer', 'story', 'gameplay', 'modding', 'screenshots', 'reaction', 'footer'];

    fullpaged = false;

    killReactLoop = true;

    throttleResize = throttle(() => {
        this.setSize();
    }, this.props.mobile || this.props.tablet ? 50 : 300, {leading: false});

    componentDidMount() {
        if (!this.fullpaged) {
            setTimeout(() => {
                this.setupFullpage();
            }, 100);
        } else {
            this.rebuild();
        }

        this.fullpaged = true;
        this.setSize();
        root.addEventListener('resize', this.throttleResize);
    }

    componentWillReceiveProps(nextProps) {
        //check for reaction section to update anchors
        const anchors = this.newAnchors;

        if (this.props.location.hash !== nextProps.location.hash) {
            const anchor = nextProps.location.hash.replace('#', '');
            let sectionNum = null;
            for (let i = 0; i < anchors.length; i++) {
                if (anchors[i] === anchor) {
                    sectionNum = i;
                    break;
                }
            }
            if (sectionNum !== null) {
                $.fn.fullpage.moveTo(sectionNum + 1);
            }
        }
    }

    componentWillUnmount() {
        if ($.fn.fullpage) {
            $.fn.fullpage.destroy('all');
        }
        root.removeEventListener('resize', this.throttleResize);
    }

    rebuild = () => {
        if ($.fn.fullpage) {
            $.fn.fullpage.reBuild();
            if (process.env.NODE_ENV !== 'production') {
                console.debug('Fullpage reBuild');
            }
            this.setState({
                rebuildDirty: !this.state.rebuildDirty //need to force a render on children
            });
        }
    };

    setupFullpage = () => {
        //https://github.com/alvarotrigo/fullPage.js
        const anchors = this.newAnchors;

        let touchSensitivity = 10;
        if (this.props.tablet) {
            touchSensitivity = 7;
        }

        const $home = $('#homeFullPage');
        $home.fullpage({
            navigation: false,
            css3: true,
            slidesNavigation: true,
            slidesNavPosition: 'bottom',
            anchors,
            touchSensitivity,
            normalScrollElementTouchThreshold: 1,
            continuousHorizontal: true,
            loopHorizontal: true,
            scrollingSpeed: 700,
            fitToSection: true,
            fitToSectionDelay: 250,

            keyboardScrolling: true,
            animateAnchor: true,
            controlArrows: false,

            afterRender: this.afterRender,
            onLeave: this.onLeave,
            onSlideLeave: this.onSlideLeave,
            afterResize: this.afterResize
        });
    };

    afterResize = () => {
        if (process.env.NODE_ENV !== 'production') {
            console.debug('Fullpage afterResize');
        }
        setTimeout(() => {
            this.setState({
                resizeDirty: !this.state.resizeDirty //need to force a render on children
            });
        }, 500);
    };

    setSize = () => {
        const height = getWindowHeight();
        const width = getWindowWidth();
        if (width !== this.state.width || height !== this.state.height) {
            this.setState({
                height,
                width
            });
            if (this.state.width !== -1 && this.state.height !== -1) {
                this.rebuild();
            }
        }
    };

    afterRender = () => {
        if (process.env.NODE_ENV !== 'production') {
            console.debug('Fullpage afterRender');
        }
        setTimeout(() => {
            FullpageActions.setLoaded.defer();
            this.setState({
                resizeDirty: !this.state.resizeDirty //need to force a render on children
            });
        }, 1000);
    };

    onLeave = (index, nextIndex, direction) => {
        if (process.env.NODE_ENV !== 'production') {
            console.debug(`Fullpage section loading... ${nextIndex}`);
        }
        FullpageActions.setLoadingSection.defer(nextIndex);
        //this.setReactionLoop(nextIndex);
    };

    onSlideLeave = (anchorLink, index, slideIndex, direction, nextSlideIndex) => {
        if (process.env.NODE_ENV !== 'production') {
            console.debug(`Fullpage slide loading... ${nextSlideIndex}`);
        }
        FullpageActions.setLoadingSlide.defer({sectionNum: index, slideNum: nextSlideIndex});
    };

    reactLoopRunning = false;

    /**
     * Should start a loop when on the reaction section to move the slides right.
     * Should also kill that loop when another section is selected and only ever have one instance of the loop running.
     */
    setReactionLoop = (nextIndex) => {
        const reactionSectionIndex = 6;

        const move = () => {
            if (this.killReactLoop) {
                if (process.env.NODE_ENV !== 'production') {
                    console.debug('setReactionLoop() return - need to kill');
                }
                this.reactLoopRunning = false;
                return;
            }

            this.reactLoopRunning = true;

            const slideIndexBefore = this.props.fullpageStore.loadingSlides[6];
            setTimeout(() => {
                if (!this.killReactLoop) {
                    let currentSlideIndex = this.props.fullpageStore.loadingSlides[6];
                    if (currentSlideIndex < 0) {
                        currentSlideIndex = this.props.homeStore.reactions.length - 1;
                    }

                    if (slideIndexBefore === currentSlideIndex || slideIndexBefore === undefined || currentSlideIndex === undefined) {
                        if (process.env.NODE_ENV !== 'production') {
                            console.debug('setReactionLoop() moving Reaction section right', this.killReactLoop);
                        }
                        $.fn.fullpage.moveSlideRight();
                        setTimeout(() => { move(); }, 250); //ensure the slide is moved before loop starts again
                    } else {
                        move();
                    }
                } else {
                    this.reactLoopRunning = false;
                    if (process.env.NODE_ENV !== 'production') {
                        console.debug('setReactionLoop() KILL Reaction autoplay');
                    }
                }
            }, 7000);
        };

        if (reactionSectionIndex === nextIndex) {
            this.killReactLoop = false;
            if (this.reactLoopRunning) {
                if (process.env.NODE_ENV !== 'production') {
                    console.debug('setReactionLoop() return - already running');
                }
                return;
            }
            move();
        } else {
            this.killReactLoop = true;
            if (process.env.NODE_ENV !== 'production') {
                console.debug('setReactionLoop() this.killReactLoop true');
            }
        }
    };

    render() {
        const {fullpageStore, localizationStore, homeStore} = this.props;
        const messages = localizationStore ? localizationStore.messages : {};
        const activeSection = fullpageStore.loadingSection;
        const activeSlides = fullpageStore.loadingSlides;
        const fullpageLoaded = fullpageStore.fullPageLoaded;
        const sections = [{
            title: messages['epic.odin.home.title'] || 'Home',
            anchor: 'recall'
        }, {
            title: messages['epic.odin.trailer.title'] || 'Trailer',
            anchor: 'trailer'
        }, {
            title: messages['epic.odin.story.title'] || 'Story',
            anchor: 'story'
        }, {
            title: messages['epic.odin.gameplay.title'] || 'Gameplay',
            anchor: 'gameplay'
        }, {
            title: messages['epic.odin.screenshots.title'] || 'Screenshots',
            anchor: 'screenshots'
        }, {
            title: messages['epic.odin.reactions.title'] || 'Reaction',
            anchor: 'reaction'
        }];

        const header = homeStore.header || {};

        let ios = false;
        try {
            ({ios} = root._epicGamesNav.browser);
        } catch (e) {
            console.error('Failed to read ios bool from browser');
        }

        let footerActive = 6;
        footerActive = 7;
        sections.splice(4, 0, {
            title: messages['epic.odin.mods.title'] || 'Modding',
            anchor: 'modding'
        });

        let invert = true;
        if (activeSection === 1 || activeSection === 5 || activeSection === 6) {
            invert = false;
        }
        return (
            <div id="homeView" className={`home-view-container ${ios ? 'ios' : ''}`}>
                {header.powerText ? (
                    <h4 className={cx('powerText', {invert})}>{header.powerText}</h4>
                ) : null}
                <SectionNav
                    initLoad={fullpageLoaded}
                    sections={sections}
                    loadingSection={activeSection} />

                <div id="homeFullPage">
                    <RecallSection
                        initLoad={fullpageLoaded}
                        active={activeSection === 1}
                        item={header}
                        mobile={this.props.mobile}
                        tablet={this.props.tablet}
                        dirty={this.state.resizeDirty} />

                    <TrailerSection
                        trailerData={homeStore.trailer}
                        active={activeSection === 2}
                        height={this.state.height}
                        width={this.state.width}
                        mobile={this.props.mobile}
                        tablet={this.props.tablet} />

                    <StorySection
                        storyData={homeStore.story}
                        active={activeSection === 3}
                        height={this.state.height}
                        width={this.state.width}
                        mobile={this.props.mobile}
                        tablet={this.props.tablet}
                        rebuildDirty={this.state.rebuildDirty} />

                    <ControlSection
                        controlsData={homeStore.gameplay}
                        active={activeSection === 4}
                        height={this.state.height}
                        width={this.state.width}
                        mobile={this.props.mobile}
                        tablet={this.props.tablet}
                        rebuildDirty={this.state.rebuildDirty} />

                    <ModSection
                        modsData={homeStore.mods}
                        active={activeSection === 5}
                        activeSlide={activeSlides[5] || 0}
                        height={this.state.height}
                        width={this.state.width}
                        dirty={this.state.resizeDirty}
                        footerActive={activeSection === footerActive}
                        mobile={this.props.mobile}
                        tablet={this.props.tablet} />

                    <ScreenshotSection
                        screenshots={homeStore.screenshots}
                        active={activeSection === 6}
                        footerActive={activeSection === footerActive && !homeStore.reactions}
                        height={this.state.height}
                        width={this.state.width}
                        mobile={this.props.mobile}
                        tablet={this.props.tablet}
                        activeSlide={activeSlides[6] || 0}
                        rebuildDirty={this.state.rebuildDirty}
                        title={messages['epic.odin.screenshots.title']}
                        description={homeStore.screenshotDescription} />

                    <ReactionsSection
                        reactionsData={homeStore.reactions}
                        active={activeSection === 7}
                        activeSlide={activeSlides[7] || 0}
                        footerActive={activeSection === footerActive}
                        mobile={this.props.mobile}
                        tablet={this.props.tablet} />

                    <Footer fullpage={true} />
                </div>
            </div>
        );
    }
}
